import {RecipeCarouselV2Macro, RecipeCarouselV2Recipe} from '@mfb/lego';
import {sortBy} from 'lodash';

export interface MacroDefinition {
  code: string;
  label: string;
  unit?: string;
}

export const MACRO_DEFINITIONS: Array<MacroDefinition> = [
  {code: 'ENRGY_KCAL_SV', label: 'Cal'},
  {code: 'PROTEIN_SV', label: 'P', unit: 'g'},
  {code: 'CARBS_OTHER_SV', label: 'C', unit: 'g'},
];

export interface CarouselV2BadgeResponse {
  description?: string;
  hex?: string;
}

export interface CarouselV2NutritionResponse {
  code?: string;
  label?: string;
  value?: number;
  unit?: string;
}

export interface CarouselV2SurchargeResponse {
  listPrice?: number;
  salePrice?: number;
  hasDiscount?: boolean;
}

export interface CarouselV2IngredientResponse {
  ingredientType?: number;
  description?: string;
  allergensContains?: string;
  allergensMayContain?: string;
}

export interface CarouselV2IngredientGroupResponse {
  name?: string;
  ingredients?: Array<CarouselV2IngredientResponse>;
}

export interface CarouselV2RecipeResponse {
  recipeNumber?: string;
  recipeVersion?: string;
  recipePartition?: string;
  title?: string;
  subTitle?: string;
  description?: string;
  webImageUrl?: string;
  webLargeImageUrl?: string;
  numberOfServesValue?: number;
  readyInTimeMinutes?: number;
  consumption?: string;
  composition?: string;
  tags?: Array<string>;
  allergensContains?: Array<string>;
  allergensMayContain?: Array<string>;
  ingredientGroups?: Array<CarouselV2IngredientGroupResponse>;
  badge?: Array<CarouselV2BadgeResponse>;
  preferenceCodes?: Array<string>;
  nutritionalInformation?: Array<CarouselV2NutritionResponse>;
  surcharge?: CarouselV2SurchargeResponse;
  sortOrder?: number;
  isSoldOut?: boolean;
}

export interface CarouselV2ProductDetailsResponse {
  childRecipesCollection?: Array<CarouselV2RecipeResponse>;
}

export interface CarouselV2Recipe extends RecipeCarouselV2Recipe {
  recipeNumber: string;
  recipeVersion: string;
  recipePartition: string;
  preferenceCodes: Array<string>;
  source: CarouselV2RecipeResponse;
}

export interface MapRecipeCarouselV2Options {
  includeSurchargeRecipes: boolean;
  orderValues?: Array<string>;
}

export const getSurchargeAmount = (surcharge?: CarouselV2SurchargeResponse): number => {
  if (!surcharge) {
    return 0;
  }

  return (surcharge.hasDiscount ? surcharge.salePrice : surcharge.listPrice) ?? 0;
};

export const getMacros = (
  info: Array<CarouselV2NutritionResponse> | undefined,
  definitions: Array<MacroDefinition> = MACRO_DEFINITIONS
): Array<RecipeCarouselV2Macro> =>
  definitions.flatMap(definition => {
    const value = info?.find(n => n.code === definition.code)?.value;

    return value === undefined || value === null
      ? []
      : [{value: Math.round(value), unit: definition.unit, label: definition.label}];
  });

export const parseRecipeOrderValues = (content?: string): Array<string> | undefined => {
  if (!content) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content);
    const list = Array.isArray(parsed)
      ? parsed
      : Object.values(parsed ?? {}).find(Array.isArray);

    return Array.isArray(list) && list.length > 0 ? list.map(String) : undefined;
  } catch {
    return undefined;
  }
};

export const orderCarouselV2Recipes = (
  recipes: Array<CarouselV2RecipeResponse>,
  orderValues?: Array<string>
): Array<CarouselV2RecipeResponse> => {
  if (!orderValues || orderValues.length === 0) {
    return sortBy(recipes, r => r.sortOrder ?? Number.MAX_SAFE_INTEGER);
  }

  const indexByRecipeNumber = new Map(orderValues.map((v, i) => [v, i]));

  return sortBy(recipes, r => indexByRecipeNumber.get(String(r.recipeNumber)) ?? Number.MAX_SAFE_INTEGER);
};

export const mapProductDetailsToCarouselV2Recipes = (
  response: CarouselV2ProductDetailsResponse | null | undefined,
  options: MapRecipeCarouselV2Options
): Array<CarouselV2Recipe> =>
  orderCarouselV2Recipes(response?.childRecipesCollection ?? [], options.orderValues)
    .filter(r => !r.isSoldOut)
    .filter(r => options.includeSurchargeRecipes || getSurchargeAmount(r.surcharge) === 0)
    .map(r => {
      const badge = r.badge?.find(b => b.description);

      return {
        id: `${r.recipeNumber}-${r.recipeVersion}-${r.recipePartition}`,
        name: r.title ?? '',
        imageSrc: r.webImageUrl ?? undefined,
        imageAlt: r.title ?? '',
        badge: badge && {label: badge.description ?? '', colour: badge.hex ?? undefined},
        macros: getMacros(r.nutritionalInformation),
        recipeNumber: r.recipeNumber ?? '',
        recipeVersion: r.recipeVersion ?? '',
        recipePartition: r.recipePartition ?? '',
        preferenceCodes: r.preferenceCodes ?? [],
        source: r,
      };
    });
